import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip, BarChart, Bar, XAxis, YAxis, CartesianGrid, LineChart, Line } from 'recharts';
import { Users, BookOpen, Award, TrendingUp } from 'lucide-react';
import type { UserRoleData, CategoryCompletion, CertificateTrend } from '@/services/staffAnalyticsDataService';

interface StaffAnalyticsChartsProps {
  analytics: {
    total_users: number;
    active_assignments: number;
    completed_assignments: number;
    completion_rate: number;
    total_certificates: number;
    pending_approvals: number;
  };
  userRoleData?: UserRoleData;
  categoryData?: CategoryCompletion[];
  certificateTrend?: CertificateTrend[];
}

const StaffAnalyticsCharts: React.FC<StaffAnalyticsChartsProps> = ({ 
  analytics, 
  userRoleData: userRoleDataProp,
  categoryData: categoryDataProp,
  certificateTrend: certificateTrendProp 
}) => {
  // User Role Distribution - use real data from props or fallback to empty
  const userRoleData = userRoleDataProp ? [
    { name: 'Users', value: userRoleDataProp.users, color: 'hsl(var(--primary))' },
    { name: 'Staff', value: userRoleDataProp.staff, color: 'hsl(var(--secondary))' },
    { name: 'Admins', value: userRoleDataProp.admin, color: 'hsl(var(--accent))' }
  ].filter(item => item.value > 0) : [];

  // Assignment Status Breakdown
  const assignmentStatusData = [
    { name: 'Completed', value: analytics.completed_assignments, color: 'hsl(var(--success))' },
    { name: 'In Progress', value: analytics.active_assignments - analytics.completed_assignments, color: 'hsl(var(--primary))' },
    { name: 'Not Started', value: Math.floor(analytics.active_assignments * 0.15), color: 'hsl(var(--muted-foreground))' }
  ].filter(item => item.value > 0);

  // Course Completion by Category - use real data from props
  const categoryData = (categoryDataProp || []).map(cat => ({
    category: cat.category,
    enrollments: cat.enrollments,
    completions: cat.completions
  }));

  // Monthly Certificate Issuance - use real data from props
  const certificateTrendData = certificateTrendProp || [];

  const COLORS = {
    primary: 'hsl(var(--primary))',
    secondary: 'hsl(var(--secondary))',
    accent: 'hsl(var(--accent))',
    success: 'hsl(var(--success))',
    warning: 'hsl(var(--warning))',
    muted: 'hsl(var(--muted-foreground))'
  };

  return (
    <div className="space-y-6">
      {/* Top Row - Pie Charts */}
      <div className="grid gap-6 md:grid-cols-2">
        <Card className="hover:shadow-md transition-shadow">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="h-5 w-5" />
              User Role Distribution
            </CardTitle>
          </CardHeader>
        <CardContent>
            {userRoleData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={userRoleData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {userRoleData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex items-center justify-center h-[300px] text-muted-foreground">
                Loading role distribution data...
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="hover:shadow-md transition-shadow">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BookOpen className="h-5 w-5" />
              Assignment Status Breakdown
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={assignmentStatusData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {assignmentStatusData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      {/* Middle Row - Bar Chart */}
      <Card className="hover:shadow-md transition-shadow">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Award className="h-5 w-5" />
            Course Completion by Category
          </CardTitle>
        </CardHeader>
        <CardContent>
          {categoryData.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={categoryData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="category" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar dataKey="enrollments" fill={COLORS.secondary} radius={[8, 8, 0, 0]} name="Enrollments" />
                <Bar dataKey="completions" fill={COLORS.success} radius={[8, 8, 0, 0]} name="Completions" />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex items-center justify-center h-[300px] text-muted-foreground">
              Loading category data...
            </div>
          )}
        </CardContent>
      </Card>

      {/* Bottom Row - Line Chart */}
      <Card className="hover:shadow-md transition-shadow">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TrendingUp className="h-5 w-5" />
            Monthly Certificate Issuance Trend
          </CardTitle>
        </CardHeader>
        <CardContent>
          {certificateTrendData.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={certificateTrendData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Line 
                  type="monotone" 
                  dataKey="certificates" 
                  stroke={COLORS.primary}
                  strokeWidth={3}
                  name="Certificates Issued"
                />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex items-center justify-center h-[300px] text-muted-foreground">
              Loading certificate trend data...
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default StaffAnalyticsCharts;
